import { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import Box from '@mui/material/Box';
import Grid from '@mui/material/Grid';
import Typography from '@mui/material/Typography';
import Paper from '@mui/material/Paper';
import Button from '@mui/material/Button';
import TextField from '@mui/material/TextField';
import FormControl from '@mui/material/FormControl';
import InputLabel from '@mui/material/InputLabel';
import Select from '@mui/material/Select';
import MenuItem from '@mui/material/MenuItem';
import Alert from '@mui/material/Alert';
import CircularProgress from '@mui/material/CircularProgress';
import Divider from '@mui/material/Divider';
import EditIcon from '@mui/icons-material/esm/Edit';
import SaveIcon from '@mui/icons-material/esm/Save';
import CancelIcon from '@mui/icons-material/esm/Cancel';

import { PageHeader } from '../../components/layout';
import { GaugeChart, LineChart } from '../../components/charts';
import { DataTable, Column, StatusBadge, ProgressBar } from '../../components/common';
import wbsService, { WBSElement, EVMMetrics } from '../../services/api/wbsService';

export default function WBSDetailPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [wbsElement, setWBSElement] = useState<WBSElement | null>(null);
  const [evmMetrics, setEVMMetrics] = useState<EVMMetrics | null>(null);
  const [linkedTasks, setLinkedTasks] = useState<any[]>([]);
  const [isEditing, setIsEditing] = useState(false);

  const fetchWBSData = async () => {
    if (!id) return;

    try {
      setLoading(true);
      setError(null);

      const [elementData, evmData, tasksData] = await Promise.all([
        wbsService.getById(id),
        wbsService.getEVMMetrics(id),
        wbsService.getLinkedTasks(id),
      ]);

      setWBSElement(elementData);
      setEVMMetrics(evmData);
      setLinkedTasks(tasksData);
    } catch (err: any) {
      console.error('Failed to fetch WBS data:', err);
      setError(err.response?.data?.message || 'Failed to load WBS element');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchWBSData();
  }, [id]);

  const handleSave = async () => {
    if (!wbsElement || !id) return;

    try {
      await wbsService.update(id, {
        name: wbsElement.name,
        description: wbsElement.description,
        status: wbsElement.status,
        priority: wbsElement.priority,
        startDate: wbsElement.startDate,
        endDate: wbsElement.endDate,
        budget: wbsElement.budget,
      });
      setIsEditing(false);
      await fetchWBSData();
    } catch (err: any) {
      console.error('Failed to update WBS element:', err);
      setError(err.response?.data?.message || 'Failed to update WBS element');
    }
  };

  const taskColumns: Column<any>[] = [
    {
      id: 'id',
      label: 'Task ID',
      minWidth: 100,
    },
    {
      id: 'title',
      label: 'Title',
      minWidth: 200,
    },
    {
      id: 'status',
      label: 'Status',
      minWidth: 120,
      format: (value: string) => <StatusBadge status={value} />,
    },
    {
      id: 'progress',
      label: 'Progress',
      minWidth: 150,
      format: (value: number) => (
        <Box sx={{ width: '100%' }}>
          <ProgressBar value={value} height={6} showPercentage={false} />
        </Box>
      ),
    },
  ];

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 400 }}>
        <CircularProgress size={60} />
      </Box>
    );
  }

  if (error || !wbsElement || !evmMetrics) {
    return (
      <Box>
        <PageHeader title="WBS Element" />
        <Alert severity="error">{error || 'WBS element not found'}</Alert>
      </Box>
    );
  }

  return (
    <Box>
      {/* Header */}
      <PageHeader
        title={`${wbsElement.code} - ${wbsElement.name}`}
        breadcrumbs={[
          { label: 'Home', path: '/' },
          { label: 'WBS', path: '/wbs' },
          { label: wbsElement.code },
        ]}
        actions={
          isEditing ? (
            <>
              <Button
                variant="outlined"
                size="small"
                startIcon={<CancelIcon />}
                onClick={() => {
                  setIsEditing(false);
                  fetchWBSData();
                }}
              >
                Cancel
              </Button>
              <Button variant="contained" size="small" startIcon={<SaveIcon />} onClick={handleSave}>
                Save
              </Button>
            </>
          ) : (
            <Button
              variant="contained"
              size="small"
              startIcon={<EditIcon />}
              onClick={() => setIsEditing(true)}
            >
              Edit
            </Button>
          )
        }
      />

      {/* WBS Information */}
      <Paper sx={{ p: 3, mb: 3 }}>
        <Typography variant="h6" fontWeight="bold" gutterBottom>
          WBS Information
        </Typography>
        <Divider sx={{ mb: 2 }} />

        <Grid container spacing={3}>
          <Grid item xs={12} md={6}>
            <TextField
              fullWidth
              label="Code"
              value={wbsElement.code}
              disabled
              size="small"
            />
          </Grid>
          <Grid item xs={12} md={6}>
            <TextField
              fullWidth
              label="Level"
              value={wbsElement.level}
              disabled
              size="small"
            />
          </Grid>

          <Grid item xs={12}>
            <TextField
              fullWidth
              label="Name"
              value={wbsElement.name}
              onChange={(e) => setWBSElement({ ...wbsElement, name: e.target.value })}
              disabled={!isEditing}
              size="small"
            />
          </Grid>

          <Grid item xs={12} md={6}>
            <FormControl fullWidth size="small">
              <InputLabel>Type</InputLabel>
              <Select value={wbsElement.type} disabled label="Type">
                <MenuItem value="PROJECT">Project</MenuItem>
                <MenuItem value="PHASE">Phase</MenuItem>
                <MenuItem value="DELIVERABLE">Deliverable</MenuItem>
                <MenuItem value="WORK_PACKAGE">Work Package</MenuItem>
                <MenuItem value="MILESTONE">Milestone</MenuItem>
              </Select>
            </FormControl>
          </Grid>

          <Grid item xs={12} md={6}>
            <FormControl fullWidth size="small">
              <InputLabel>Status</InputLabel>
              <Select
                value={wbsElement.status}
                onChange={(e) =>
                  setWBSElement({ ...wbsElement, status: e.target.value as any })
                }
                disabled={!isEditing}
                label="Status"
              >
                <MenuItem value="DRAFT">Draft</MenuItem>
                <MenuItem value="ACTIVE">Active</MenuItem>
                <MenuItem value="COMPLETED">Completed</MenuItem>
                <MenuItem value="CANCELLED">Cancelled</MenuItem>
              </Select>
            </FormControl>
          </Grid>

          <Grid item xs={12}>
            <TextField
              fullWidth
              label="Description"
              value={wbsElement.description || ''}
              onChange={(e) => setWBSElement({ ...wbsElement, description: e.target.value })}
              disabled={!isEditing}
              multiline
              rows={3}
              size="small"
            />
          </Grid>

          <Grid item xs={12} md={6}>
            <TextField
              fullWidth
              label="Start Date"
              type="date"
              value={wbsElement.startDate?.split('T')[0] || ''}
              onChange={(e) => setWBSElement({ ...wbsElement, startDate: e.target.value })}
              disabled={!isEditing}
              InputLabelProps={{ shrink: true }}
              size="small"
            />
          </Grid>

          <Grid item xs={12} md={6}>
            <TextField
              fullWidth
              label="End Date"
              type="date"
              value={wbsElement.endDate?.split('T')[0] || ''}
              onChange={(e) => setWBSElement({ ...wbsElement, endDate: e.target.value })}
              disabled={!isEditing}
              InputLabelProps={{ shrink: true }}
              size="small"
            />
          </Grid>

          <Grid item xs={12} md={4}>
            <TextField
              fullWidth
              label="Duration (days)"
              value={wbsElement.duration || ''}
              disabled
              size="small"
            />
          </Grid>

          <Grid item xs={12} md={4}>
            <FormControl fullWidth size="small">
              <InputLabel>Progress Method</InputLabel>
              <Select
                value={wbsElement.progressMethod}
                disabled
                label="Progress Method"
              >
                <MenuItem value="AUTO">Auto (from children)</MenuItem>
                <MenuItem value="MANUAL">Manual</MenuItem>
                <MenuItem value="WEIGHTED">Weighted</MenuItem>
              </Select>
            </FormControl>
          </Grid>

          <Grid item xs={12} md={4}>
            <Box>
              <Typography variant="caption" color="text.secondary">
                Progress
              </Typography>
              <ProgressBar value={wbsElement.progress} />
            </Box>
          </Grid>
        </Grid>
      </Paper>

      {/* EVM Metrics */}
      <Paper sx={{ p: 3, mb: 3 }}>
        <Typography variant="h6" fontWeight="bold" gutterBottom>
          Earned Value Management Metrics
        </Typography>
        <Divider sx={{ mb: 3 }} />

        {/* Primary Metrics */}
        <Grid container spacing={3} sx={{ mb: 3 }}>
          <Grid item xs={12} md={4}>
            <Paper variant="outlined" sx={{ p: 2, textAlign: 'center' }}>
              <Typography variant="caption" color="text.secondary">
                PLANNED VALUE (PV)
              </Typography>
              <Typography variant="h4" fontWeight="bold">
                ${(evmMetrics.pv / 1000000).toFixed(2)}M
              </Typography>
              <Typography variant="caption" color="text.secondary">
                100% of budget
              </Typography>
            </Paper>
          </Grid>

          <Grid item xs={12} md={4}>
            <Paper variant="outlined" sx={{ p: 2, textAlign: 'center' }}>
              <Typography variant="caption" color="text.secondary">
                EARNED VALUE (EV)
              </Typography>
              <Typography variant="h4" fontWeight="bold" color="primary.main">
                ${(evmMetrics.ev / 1000000).toFixed(2)}M
              </Typography>
              <Typography variant="caption" color="text.secondary">
                {((evmMetrics.ev / evmMetrics.pv) * 100).toFixed(0)}% earned
              </Typography>
            </Paper>
          </Grid>

          <Grid item xs={12} md={4}>
            <Paper variant="outlined" sx={{ p: 2, textAlign: 'center' }}>
              <Typography variant="caption" color="text.secondary">
                ACTUAL COST (AC)
              </Typography>
              <Typography variant="h4" fontWeight="bold" color="error.main">
                ${(evmMetrics.ac / 1000000).toFixed(2)}M
              </Typography>
              <Typography variant="caption" color="text.secondary">
                {((evmMetrics.ac / evmMetrics.pv) * 100).toFixed(0)}% spent
              </Typography>
            </Paper>
          </Grid>
        </Grid>

        {/* Performance Indices */}
        <Grid container spacing={3} sx={{ mb: 3 }}>
          <Grid item xs={12} md={4}>
            <Paper variant="outlined" sx={{ p: 2 }}>
              <Typography variant="body2" fontWeight="medium" gutterBottom>
                Cost Performance Index (CPI)
              </Typography>
              <Box sx={{ display: 'flex', justifyContent: 'center', mb: 1 }}>
                <GaugeChart
                  value={evmMetrics.cpi}
                  max={1.5}
                  height={120}
                  thresholds={{
                    low: { max: 0.9, color: '#d32f2f' },
                    medium: { max: 1.1, color: '#66bb6a' },
                    high: { color: '#ffa726' },
                  }}
                />
              </Box>
              <Typography variant="caption" color="text.secondary" sx={{ textAlign: 'center', display: 'block' }}>
                {evmMetrics.cpi < 1 ? `${((1 - evmMetrics.cpi) * 100).toFixed(0)}% over budget` : 'Under budget'}
              </Typography>
            </Paper>
          </Grid>

          <Grid item xs={12} md={4}>
            <Paper variant="outlined" sx={{ p: 2 }}>
              <Typography variant="body2" fontWeight="medium" gutterBottom>
                Schedule Performance Index (SPI)
              </Typography>
              <Box sx={{ display: 'flex', justifyContent: 'center', mb: 1 }}>
                <GaugeChart
                  value={evmMetrics.spi}
                  max={1.5}
                  height={120}
                  thresholds={{
                    low: { max: 0.9, color: '#d32f2f' },
                    medium: { max: 1.1, color: '#66bb6a' },
                    high: { color: '#ffa726' },
                  }}
                />
              </Box>
              <Typography variant="caption" color="text.secondary" sx={{ textAlign: 'center', display: 'block' }}>
                {evmMetrics.spi < 1 ? `${((1 - evmMetrics.spi) * 100).toFixed(0)}% behind schedule` : 'Ahead of schedule'}
              </Typography>
            </Paper>
          </Grid>

          <Grid item xs={12} md={4}>
            <Paper variant="outlined" sx={{ p: 2 }}>
              <Typography variant="body2" fontWeight="medium" gutterBottom>
                Performance Status
              </Typography>
              <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: 120 }}>
                <StatusBadge
                  status={
                    evmMetrics.performanceStatus === 'ON_TRACK'
                      ? 'ON TRACK'
                      : evmMetrics.performanceStatus === 'BEHIND_SCHEDULE'
                      ? 'BEHIND SCHEDULE'
                      : evmMetrics.performanceStatus === 'OVER_BUDGET'
                      ? 'OVER BUDGET'
                      : 'BEHIND & OVER'
                  }
                  type="custom"
                  customColor={
                    evmMetrics.performanceStatus === 'ON_TRACK'
                      ? 'success'
                      : evmMetrics.performanceStatus === 'BEHIND_AND_OVER'
                      ? 'error'
                      : 'warning'
                  }
                />
              </Box>
            </Paper>
          </Grid>
        </Grid>

        {/* Forecast Metrics */}
        <Grid container spacing={3}>
          <Grid item xs={12} md={4}>
            <Paper variant="outlined" sx={{ p: 2, textAlign: 'center', bgcolor: 'grey.50' }}>
              <Typography variant="caption" color="text.secondary">
                Estimate at Completion (EAC)
              </Typography>
              <Typography variant="h5" fontWeight="bold">
                ${(evmMetrics.eac / 1000000).toFixed(2)}M
              </Typography>
              <Typography variant="caption" color={evmMetrics.eac > evmMetrics.pv ? 'error.main' : 'success.main'}>
                {evmMetrics.eac > evmMetrics.pv ? 'Trending up ↗' : 'Trending down ↘'}
              </Typography>
            </Paper>
          </Grid>

          <Grid item xs={12} md={4}>
            <Paper variant="outlined" sx={{ p: 2, textAlign: 'center', bgcolor: 'grey.50' }}>
              <Typography variant="caption" color="text.secondary">
                Estimate to Complete (ETC)
              </Typography>
              <Typography variant="h5" fontWeight="bold">
                ${(evmMetrics.etc / 1000000).toFixed(2)}M
              </Typography>
              <Typography variant="caption" color="text.secondary">
                Remaining work
              </Typography>
            </Paper>
          </Grid>

          <Grid item xs={12} md={4}>
            <Paper variant="outlined" sx={{ p: 2, textAlign: 'center', bgcolor: evmMetrics.vac < 0 ? 'error.50' : 'success.50' }}>
              <Typography variant="caption" color="text.secondary">
                Variance at Completion (VAC)
              </Typography>
              <Typography
                variant="h5"
                fontWeight="bold"
                color={evmMetrics.vac < 0 ? 'error.main' : 'success.main'}
              >
                {evmMetrics.vac < 0 ? '-' : '+'}${Math.abs(evmMetrics.vac / 1000000).toFixed(2)}M
              </Typography>
              <Typography variant="caption" color={evmMetrics.vac < 0 ? 'error.main' : 'success.main'}>
                {evmMetrics.vac < 0 ? 'Overrun' : 'Underrun'}
              </Typography>
            </Paper>
          </Grid>
        </Grid>
      </Paper>

      {/* EVM Trend Chart */}
      <Paper sx={{ p: 3, mb: 3 }}>
        <Typography variant="h6" fontWeight="bold" gutterBottom>
          EVM Trend Chart
        </Typography>
        <Divider sx={{ mb: 2 }} />
        <LineChart
          labels={['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun']}
          datasets={[
            {
              label: 'Planned Value (PV)',
              data: [5, 10, 15, 20, 25, 30],
              borderColor: '#ffa726',
              backgroundColor: '#ffa72630',
              fill: true,
            },
            {
              label: 'Earned Value (EV)',
              data: [4, 9, 13, 18, 22, 27],
              borderColor: '#1976d2',
              backgroundColor: '#1976d230',
              fill: true,
            },
            {
              label: 'Actual Cost (AC)',
              data: [5.2, 10.5, 15.8, 21.2, 26.5, 32],
              borderColor: '#d32f2f',
              backgroundColor: '#d32f2f30',
              fill: true,
            },
          ]}
          height={300}
          yAxisLabel="Cost ($M)"
        />
      </Paper>

      {/* Linked Tasks */}
      <Paper sx={{ p: 3 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
          <Typography variant="h6" fontWeight="bold">
            Linked Tasks
          </Typography>
          <Button variant="outlined" size="small" onClick={() => console.log('Link new task')}>
            + Link Task
          </Button>
        </Box>
        <Divider sx={{ mb: 2 }} />

        {linkedTasks.length === 0 ? (
          <Alert severity="info">No tasks linked to this WBS element.</Alert>
        ) : (
          <DataTable
            columns={taskColumns}
            data={linkedTasks}
            pagination={false}
            searchable={false}
            onRowClick={(task: any) => navigate(`/tasks/${task.id}`)}
          />
        )}
      </Paper>
    </Box>
  );
}
